import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import DailyRemainingImpressionsFields from '../../../../../Google/UploadGoogleAdCreative/UploadGoogleAdCreativeForm/CampaignForm/FormFields/DailyRemainingImpressionsFields'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import useRightBudgetAndImpressionsCalculation from '../../../../UploadAdCreative/useRightBudgetAndImpressionsCalculation'

import { GOOGLE_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

import {
  calculateBudgetFromImpressions,
  calculateImpressionsFromBudget
} from '../../../../UploadAdCreative/ImpressionsBudgetSection/helpers'
import { clearUpdateCampaign, updateCampaign } from '../../../../../../modules/actions/campaigns'
import { rightSelector } from '../../../../../../modules/selectors/rights'
import {
  campaignUpdateErrorSelector,
  campaignUpdateIsLoadingSelector,
  campaignWasUpdatedSelector
} from '../../../../../../modules/selectors/campaigns'
import { UPDATE_COMBINED_CAMPAIGN } from '../../../../../../modules/actions/combinedData'

import { AMOUNT_MICROS, BUDGET_DAILY, CAMPAIGN_BUDGET } from '../../../../CampaignForms/fields'
import { IMPRESSIONS } from '../../../../../Google/UploadGoogleAdCreative/UploadGoogleAdCreativeForm/CampaignForm/fields'
import { getValidationSchema } from './validation'

const ImpressionsBudgetDailySectionForm = ({ editItemData: campaign, title, ...formProps }) => {
  const dispatch = useDispatch()

  const campaignWasUpdated = useSelector(campaignWasUpdatedSelector)

  const { ad_account_id: adAccountId } = campaign

  const { remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const { bid_cap: rightBidCap } = useSelector(rightSelector)

  const handleSubmit = useCallback(
    values => {
      const dailyBudget = calculateBudgetFromImpressions({
        impressions: values[IMPRESSIONS],
        rightBidCap
      })

      const updateCampaignData = {
        account: adAccountId,
        [CAMPAIGN_BUDGET]: {
          [AMOUNT_MICROS]: dailyBudget
        },
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(
        updateCampaign(updateCampaignData, campaign.id, GOOGLE_PLATFORM, {
          updateType: UPDATE_COMBINED_CAMPAIGN
        })
      )
    },
    [dispatch, campaign.id, title, adAccountId, rightBidCap]
  )

  const initialCampaignBudgetDaily = useMemo(() => {
    // Recently created google campaign has budget in different field (campaign.campaign_budget.amount_micros)
    return campaign?.[BUDGET_DAILY] || campaign?.[CAMPAIGN_BUDGET]?.[AMOUNT_MICROS]
  }, [campaign])

  const initialCampaignImpressionsDaily = useMemo(() => {
    return calculateImpressionsFromBudget({
      budget: initialCampaignBudgetDaily,
      rightBidCap
    })
  }, [initialCampaignBudgetDaily, rightBidCap])

  const initialValues = useMemo(() => {
    return {
      [IMPRESSIONS]: String(initialCampaignImpressionsDaily) || ''
    }
  }, [initialCampaignImpressionsDaily])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: getValidationSchema({
      remainingImpressions,
      campaign,
      initialCampaignImpressionsDaily
    })
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateCampaign = useCallback(() => {
    dispatch(clearUpdateCampaign())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={campaignWasUpdated}
      clearEditItem={handleClearUpdateCampaign}
      errorSelector={campaignUpdateErrorSelector}
      isLoadingSelector={campaignUpdateIsLoadingSelector}
      {...formProps}
    >
      <DailyRemainingImpressionsFields
        formik={formik}
        remainingImpressions={remainingImpressions}
        startDate={campaign.date_start}
        endDate={campaign.date_stop}
      />
    </EditForm>
  )
}

export default ImpressionsBudgetDailySectionForm
